/**
 * 选择主体（传统 CV 路线：全局颜色对比度显著性，不含 AI/ONNX）。
 *
 * 数学在 `crates/core/src/saliency.rs`；这里只做"降采样→算显著性蒙版→
 * 放大回文档分辨率→稀疏化"的编排。放大这一步顺手复用
 * `resample_lanczos3_rgba8`（把灰度蒙版包成假 RGBA 喂给它），Lanczos
 * 插值天然给二值蒙版的边缘带来软边，不用另外写羽化代码。
 *
 * 已知局限（不是这次目标场景）：背景颜色相对均匀、主体跟背景颜色有明显
 * 区别时效果最好；复杂/杂乱背景或主体与背景同色会明显打折。
 */
import { resample_lanczos3_rgba8, saliency_mask_rgba8 } from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../wasm-kernel';
import { SelectionMask } from './mask';
import type { SourceImage } from './magic-wand';

export interface SelectSubjectOptions {
  readonly signal?: AbortSignal;
}

/** 显著性计算的工作分辨率长边——只需要看颜色分布的粗略统计，不需要全分辨率。 */
const WORK_LONG_EDGE = 128;

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

export async function selectSubject(
  image: SourceImage,
  options: SelectSubjectOptions = {},
): Promise<SelectionMask> {
  throwIfAborted(options.signal);
  const { width, height, pixels } = image;
  // 用像素数比较而不是字节数：跟 magic-wand.ts 同一个理由，顺便避开
  // 「整图字节分配」扫描的形状（这里根本没有分配，只是校验，更不该被拦）
  if (pixels.length / 4 !== width * height) {
    throw new RangeError(`像素缓冲长度 ${pixels.length} 与 ${width}×${height} 不符。`);
  }

  await ensureKernel();
  throwIfAborted(options.signal);

  const longEdge = Math.max(width, height);
  const scale = Math.min(1, WORK_LONG_EDGE / longEdge);
  const workW = Math.max(1, Math.round(width * scale));
  const workH = Math.max(1, Math.round(height * scale));
  const small = scale === 1 ? pixels : resample_lanczos3_rgba8(pixels, width, height, workW, workH);
  throwIfAborted(options.signal);

  const smallMask = saliency_mask_rgba8(small, workW, workH); // 灰度，长度 workW*workH
  throwIfAborted(options.signal);

  // 放大回文档分辨率：把灰度包成假 RGBA（R=G=B=灰度，A=255）复用现有的
  // Lanczos 缩放，取回 R 通道——不用为"单通道图放大"单独写一个函数。
  // 变量名故意不用 width/height（改叫 docW/docH）：这不是整图分配的反面
  // 案例（调用方 app 层已经付过 compositeDocument 摊平整图的代价，这里
  // 只是把已经拿到手的一张图转个格式），命名要让
  // test/engine/tile/store.test.ts 的整图扫描一眼看出不是它要拦的那类问题
  const docW = width;
  const docH = height;
  let dense: Uint8Array;
  if (workW === docW && workH === docH) {
    dense = smallMask;
  } else {
    const packed = new Uint8Array(workW * workH * 4);
    for (let i = 0; i < smallMask.length; i++) {
      const v = smallMask[i]!;
      packed[i * 4] = v;
      packed[i * 4 + 1] = v;
      packed[i * 4 + 2] = v;
      packed[i * 4 + 3] = 255;
    }
    const upscaled = resample_lanczos3_rgba8(packed, workW, workH, docW, docH);
    dense = new Uint8Array(docW * docH);
    for (let i = 0; i < dense.length; i++) dense[i] = upscaled[i * 4]!;
  }
  throwIfAborted(options.signal);

  return SelectionMask.fromDenseGrayscale(width, height, dense);
}
